import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  listUsers, saveUser, deleteUser,
  listRoles, saveRole, deleteRole,
  WebUser, WebRole,
} from '@/api/users';

/* ─────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────── */
function ErrMsg({ msg }: { msg: string }) {
  return msg ? <p className="text-red-600 text-sm font-georgia mt-1">{msg}</p> : null;
}

const inputCls = 'w-full border rounded px-2 py-1.5 text-sm font-georgia focus:outline-none focus:ring-1 focus:ring-teal mb-3 disabled:opacity-60 disabled:bg-gray-100';
const labelCls = 'block text-xs font-georgia text-gray-600 mb-0.5';

/* ─────────────────────────────────────────────────────────────
   Users tab
───────────────────────────────────────────────────────────── */
function UsersTab({ roles }: { roles: WebRole[] }) {
  const [users,   setUsers]   = useState<WebUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState('');

  // form
  const blankForm = { username: '', email: '', roleId: null as number | null, isActive: true, password: '' };
  const [form,      setForm]      = useState(blankForm);
  const [editing,   setEditing]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [formErr,   setFormErr]   = useState('');
  const [deleteErr, setDeleteErr] = useState('');

  const { user: me } = useAuth();

  useEffect(() => {
    listUsers()
      .then(setUsers)
      .catch(() => setErr('Failed to load users'))
      .finally(() => setLoading(false));
  }, []);

  const openNew = () => {
    setForm(blankForm);
    setEditing(true);
    setFormErr('');
  };

  const openEdit = (u: WebUser) => {
    setForm({ username: u.username, email: u.email, roleId: u.roleId, isActive: u.isActive, password: '' });
    setEditing(true);
    setFormErr('');
  };

  const isNew = !users.find((u) => u.username === form.username);

  const handleSave = async () => {
    if (!form.username.trim()) { setFormErr('Username is required'); return; }
    if (isNew && !form.password.trim()) { setFormErr('Password is required for new users'); return; }
    setSaving(true);
    setFormErr('');
    try {
      const payload = { ...form, password: form.password.trim() || undefined };
      const updated = await saveUser(payload);
      setUsers(updated);
      setForm(blankForm);
      setEditing(false);
    } catch (e: unknown) {
      setFormErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (username: string) => {
    if (username === me?.username) {
      setDeleteErr('You cannot delete your own account.');
      return;
    }
    if (!confirm(`Delete user "${username}"?`)) return;
    setDeleteErr('');
    try {
      await deleteUser(username);
      setUsers((prev) => prev.filter((u) => u.username !== username));
    } catch (e: unknown) {
      setDeleteErr(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  if (loading) return <p className="p-6 text-center text-gray-400 font-georgia text-sm">Loading users…</p>;
  if (err)     return <p className="p-6 text-center text-red-500 font-georgia text-sm">{err}</p>;

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-x-auto">
      {/* Toolbar */}
      <div className="flex justify-between items-center gap-3 p-4 border-b">
        <p className="text-xs font-bold font-georgia text-gray-400 uppercase tracking-wide">Web Users</p>
        <button
          onClick={openNew}
          className="px-4 py-2 bg-teal text-white text-sm font-georgia rounded hover:bg-teal-700 whitespace-nowrap"
        >
          + Add User
        </button>
      </div>

      {deleteErr && <p className="px-4 pt-3 text-sm text-red-600 font-georgia">{deleteErr}</p>}

      {/* Table */}
      <div className="hidden sm:block">
      <table className="w-full text-sm font-georgia">
        <thead className="bg-navy text-white text-xs uppercase">
          <tr>
            {['Username', 'Email', 'Role', 'Active', 'Actions'].map((h) => (
              <th key={h} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {users.length === 0 && (
            <tr><td colSpan={5} className="px-3 py-10 text-center text-gray-400 font-georgia text-sm">No users found</td></tr>
          )}
          {users.map((u) => (
            <tr key={u.username} className="hover:bg-gray-50">
              <td className="px-3 py-2 text-navy font-medium">{u.username}</td>
              <td className="px-3 py-2 text-gray-500">{u.email || '—'}</td>
              <td className="px-3 py-2 text-gray-500">{u.roleName || '—'}</td>
              <td className="px-3 py-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                  {u.isActive ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td className="px-3 py-2 space-x-2 whitespace-nowrap">
                <button
                  onClick={() => openEdit(u)}
                  className="text-teal hover:underline text-xs"
                >Edit</button>
                <button
                  onClick={() => handleDelete(u.username)}
                  disabled={u.username === me?.username}
                  className="text-red-500 hover:underline text-xs disabled:opacity-40"
                >Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {/* Mobile: compact cards */}
      <div className="sm:hidden divide-y divide-gray-100">
        {users.length === 0 && (
          <p className="px-3 py-10 text-center text-gray-400 font-georgia text-sm">No users found</p>
        )}
        {users.map((u) => (
          <div key={u.username} className="p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-navy font-georgia truncate">{u.username}</p>
                <p className="text-xs text-gray-500 font-georgia truncate">{u.email || '—'}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${u.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                {u.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              <span className="text-xs bg-gray-100 text-navy rounded-full px-2 py-0.5 font-georgia">{u.roleName || 'No role'}</span>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => openEdit(u)} className="flex-1 btn-secondary !py-1.5 text-xs">Edit</button>
              <button
                onClick={() => handleDelete(u.username)}
                disabled={u.username === me?.username}
                className="flex-1 btn-danger !py-1.5 text-xs disabled:opacity-40"
              >Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* Slide-in form panel */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md border shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-navy font-georgia font-bold text-lg mb-4">
              {form.username ? 'Edit User' : 'New User'}
            </h3>

            <label className={labelCls}>Username *</label>
            <input
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value.toUpperCase() }))}
              disabled={!!users.find((u) => u.username === form.username)}
              className={inputCls}
              placeholder="USERNAME"
            />

            <label className={labelCls}>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className={inputCls}
              placeholder="user@example.com"
            />

            <label className={labelCls}>
              Password {isNew ? <span className="text-red-500">*</span> : <span className="text-gray-400">(leave blank to keep current)</span>}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              className={inputCls}
              placeholder={isNew ? 'Set a password' : 'Leave blank to keep current'}
              autoComplete="new-password"
            />

            <label className={labelCls}>Role</label>
            <select
              value={form.roleId ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, roleId: e.target.value ? +e.target.value : null }))}
              className={inputCls}
            >
              <option value="">— No role —</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>

            <label className="flex items-center gap-2 text-sm font-georgia text-gray-600 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                className="accent-teal"
              />
              Active
            </label>

            <ErrMsg msg={formErr} />

            <div className="flex gap-3 mt-4">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-5 py-2 bg-teal text-white text-sm font-georgia rounded hover:bg-teal-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="flex-1 px-5 py-2 border text-sm font-georgia rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Roles tab
───────────────────────────────────────────────────────────── */
function RolesTab() {
  const [roles,   setRoles]   = useState<WebRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState('');

  const blankForm = { id: undefined as number | undefined, name: '', description: '', permissions: [] as string[] };
  const [form,      setForm]      = useState(blankForm);
  const [editing,   setEditing]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [formErr,   setFormErr]   = useState('');
  const [deleteErr, setDeleteErr] = useState('');

  useEffect(() => {
    listRoles()
      .then(setRoles)
      .catch(() => setErr('Failed to load roles'))
      .finally(() => setLoading(false));
  }, []);

  const openNew = () => {
    setForm(blankForm);
    setEditing(true);
    setFormErr('');
  };

  const openEdit = (r: WebRole) => {
    setForm({ id: r.id, name: r.name, description: r.description, permissions: [...r.permissions] });
    setEditing(true);
    setFormErr('');
  };

  const togglePerm = (p: string) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(p)
        ? f.permissions.filter((x) => x !== p)
        : [...f.permissions, p],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormErr('Role name is required'); return; }
    setSaving(true);
    setFormErr('');
    try {
      const updated = await saveRole(form);
      setRoles(updated);
      setEditing(false);
    } catch (e: unknown) {
      setFormErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (role: WebRole) => {
    const PROTECTED = ['Admin', 'Editor', 'Viewer'];
    if (PROTECTED.includes(role.name)) {
      setDeleteErr(`Cannot delete built-in role "${role.name}".`);
      return;
    }
    if (!confirm(`Delete role "${role.name}"?`)) return;
    setDeleteErr('');
    try {
      await deleteRole(role.id);
      setRoles((prev) => prev.filter((r) => r.id !== role.id));
    } catch (e: unknown) {
      setDeleteErr(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  if (loading) return <p className="p-6 text-center text-gray-400 font-georgia text-sm">Loading roles…</p>;
  if (err)     return <p className="p-6 text-center text-red-500 font-georgia text-sm">{err}</p>;

  // Group permissions by category for display
  const PERM_GROUPS: { label: string; perms: string[] }[] = [
    { label: 'Admin',         perms: ['users', 'roles'] },
    { label: 'Families',      perms: ['families', 'families.delete', 'members.delete'] },
    { label: 'Subscriptions', perms: ['subscriptions'] },
    { label: 'Cemetery',      perms: ['cemetery', 'cemetery.delete'] },
    { label: 'Export',        perms: ['export'] },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border p-4 space-y-4">
      <div className="flex justify-between items-center gap-3">
        <p className="text-xs font-bold font-georgia text-gray-400 uppercase tracking-wide">Roles &amp; Permissions</p>
        <button
          onClick={openNew}
          className="px-4 py-2 bg-teal text-white text-sm font-georgia rounded hover:bg-teal-700 whitespace-nowrap"
        >
          + Add Role
        </button>
      </div>

      {deleteErr && <p className="text-sm text-red-600 font-georgia">{deleteErr}</p>}

      <div className="space-y-3">
        {roles.length === 0 && (
          <p className="text-gray-400 font-georgia text-sm">No roles found.</p>
        )}
        {roles.map((r) => (
          <div key={r.id} className="rounded border p-4 bg-offwhite/60">
            <div className="flex justify-between items-start gap-3">
              <div>
                <p className="text-navy font-georgia font-bold">{r.name}</p>
                {r.description && <p className="text-gray-500 text-xs font-georgia mt-0.5">{r.description}</p>}
              </div>
              <div className="flex gap-3 whitespace-nowrap">
                <button onClick={() => openEdit(r)} className="text-teal hover:underline text-xs">Edit</button>
                <button onClick={() => handleDelete(r)} className="text-red-500 hover:underline text-xs">Delete</button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {r.permissions.length === 0
                ? <span className="text-gray-400 text-xs font-georgia">No permissions</span>
                : r.permissions.map((p) => (
                    <span key={p} className="text-xs font-georgia bg-teal/10 text-teal px-2 py-0.5 rounded">{p}</span>
                  ))
              }
            </div>
          </div>
        ))}
      </div>

      {/* Role form modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg border shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-navy font-georgia font-bold text-lg mb-4">
              {form.id ? 'Edit Role' : 'New Role'}
            </h3>

            <label className={labelCls}>Role Name *</label>
            <input
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className={inputCls}
              placeholder="e.g. Secretary"
            />

            <label className={labelCls}>Description</label>
            <input
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className={`${inputCls} mb-4`}
              placeholder="Optional description"
            />

            <p className="text-sm font-georgia font-bold text-gray-600 mb-2">Permissions</p>
            <div className="space-y-3">
              {PERM_GROUPS.map((grp) => (
                <div key={grp.label}>
                  <p className="text-gray-400 text-xs font-georgia uppercase tracking-wide mb-1">{grp.label}</p>
                  <div className="flex flex-wrap gap-3">
                    {grp.perms.map((p) => {
                      const checked = form.permissions.includes(p);
                      return (
                        <label key={p} className="flex items-center gap-1.5 text-sm font-georgia text-gray-700 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePerm(p)}
                            className="accent-teal"
                          />
                          {p}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <ErrMsg msg={formErr} />

            <div className="flex gap-3 mt-5">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-5 py-2 bg-teal text-white text-sm font-georgia rounded hover:bg-teal-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="flex-1 px-5 py-2 border text-sm font-georgia rounded hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Main page — two tabs
───────────────────────────────────────────────────────────── */
export default function UserManagementPage() {
  const { isAdmin } = useAuth();
  const [tab,   setTab]   = useState<'users' | 'roles'>('users');
  const [roles, setRoles] = useState<WebRole[]>([]);

  // Pre-fetch roles so UsersTab can use them in the role dropdown
  useEffect(() => {
    if (isAdmin) listRoles().then(setRoles).catch(() => {});
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 rounded p-4 text-sm text-red-700 font-georgia">
          Access denied — Admin role required.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-xl font-georgia font-bold text-navy">User Management</h1>
        <p className="text-sm text-gray-500 font-georgia">
          Manage web users, roles, and permissions
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 flex-wrap">
        {(['users', 'roles'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 rounded text-sm font-georgia transition-colors ${
              tab === t
                ? 'bg-teal text-white'
                : 'bg-white border text-gray-600 hover:bg-gray-50'
            }`}
          >
            {t === 'users' ? 'Users' : 'Roles & Permissions'}
          </button>
        ))}
      </div>

      {tab === 'users' && <UsersTab roles={roles} />}
      {tab === 'roles' && <RolesTab />}
    </div>
  );
}
